import React from 'react';
import { Calendar, Clock} from 'lucide-react';
import DeleteModal from '../cors/DeleteModal';
import { useState } from 'react';
import { BASE_URL } from '../apiConfig';
import { toast } from 'sonner';
import { useAuth } from "../context/AuthProvider";
import { Link } from 'react-router-dom';

import { useLanguage } from "../context/LanguageProvider";

export default function AnnonceList({ role, annonce, fetchAnnonces }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { token, roles } = useAuth();
  const { t } = useLanguage();

  const isActive = () => {
    const now = new Date();
    const start = new Date(annonce.date_start);
    const end = new Date(annonce.date_end);
    return now >= start && now <= end;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getDaysRemaining = () => {
    const now = new Date();
    const end = new Date(annonce.date_end);
    const diffTime = end.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const deleteAnnonce = async () => {
    setIsModalOpen(false); 

    try {
      const res = await fetch(BASE_URL+`/api/annonces/${annonce.id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      })

      if (res.ok) {
        toast.success("Annonce supprimée avec succès");
        if (fetchAnnonces) {
          fetchAnnonces();
        }
      } else {
        const errorData = await res.json();
        toast.error(errorData.message || "Erreur lors de la suppression");
      }
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const daysRemaining = getDaysRemaining();
  const active = isActive();
  const annonceColor = annonce.color || "var(--color-primary-500)"; 

  return (
    <div 
      className="rounded-2xl bg-white border border-gray-100 px-6 py-4 hover:shadow-xl transition-all duration-300 group"
      style={{ 
        borderLeft: `4px solid ${annonceColor}`
      }}
    >
      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-3">
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                active
                  ? 'bg-green-100 text-green-800'
                  : daysRemaining > 0
                  ? 'bg-orange-100 text-orange-800'
                  : 'bg-red-100 text-red-800'
              }`}
            >
              <div
                className={`w-2 h-2 rounded-full mr-2 ${
                  active ? 'bg-green-500' : daysRemaining > 0 ? 'bg-orange-500' : 'bg-red-500'
                }`}
              />
              {active ? t('annonces.status_active') : daysRemaining > 0 ? t('annonces.status_upcoming') : t('annonces.status_expired')}
            </span>
            
            {active && daysRemaining > 0 && (
              <span className="text-xs font-medium" style={{ color: annonceColor }}>
                {t('annonces.days_remaining', { count: daysRemaining })}
              </span>
            )}
          </div>
          
          <p className="text-gray-800 text-sm leading-relaxed mb-4">
            {annonce.annonce}
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 items-start lg:items-center">
          <div className="flex gap-4 text-xs text-gray-600">
            <div className="flex items-center">
              <Calendar className="h-3 w-3 mr-2" style={{ color: annonceColor }} />
              <span>{t('annonces.start_label')}: {formatDate(annonce.date_start)}</span>
            </div>
            <div className="flex items-center">
              <Clock className="h-3 w-3 mr-2" style={{ color: annonceColor }} />
              <span>{t('annonces.end_label')}: {formatDate(annonce.date_end)}</span>
            </div>
          </div>

            <div className="flex gap-2 transition-opacity duration-200">
            {
            (role === 1 || (role === 2 && roles.edit_annonce === 1)) &&
              <Link to={`/annonces/update/${annonce.id}`}>
                <button
                  className="p-2 border-2 cursor-pointer border-gray-200 hover:border-[var(--color-primary-500)] text-gray-600 hover:text-[var(--color-primary-500)] rounded-xl transition-all duration-300"
                  title="Modifier l'annonce"
                >
                  <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                </button>
              </Link>
              }
              {
                (role === 1 || (role === 2 && roles.delete_annonce === 1)) &&
                  <button
                    onClick={() => setIsModalOpen(true)}
                    className="p-2 border-2 cursor-pointer border-gray-200 hover:border-red-400 text-gray-600 hover:text-red-500 rounded-xl transition-all duration-300"
                    title="Supprimer l'annonce"
                  >
                    <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2
                          2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1
                          1 0 00-1-1h-4a1 1 0 00-1 1v3m5 0H6"
                        />
                      </svg>
                  </button>
              }
            </div>
        </div>
      </div>
      
      <DeleteModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={deleteAnnonce}
        title="Supprimer l'annonce"
        message="Êtes-vous sûr de vouloir supprimer cette annonce ?"
        itemName={annonce.annonce.substring(0, 30) + '...'}
      />
    </div>
  );
}